import { useEffect, useRef, useState } from "react";
import { PlusCircle, X, AlertTriangle, CheckCircle2, ShieldCheck, Lock } from "lucide-react";
import { smsApi, dataBr } from "./api";
import TermosDisparos from "./TermosDisparos";
import DashboardSMS from "./DashboardSMS";
import NovaCampanhaSMS from "./NovaCampanhaSMS";
import RelatorioCampanhaSMS from "./RelatorioCampanhaSMS";

/**
 * Tela unica do SMS: indicadores, visao geral e historico juntos.
 *
 * "Nova campanha" e' um botao no canto superior direito que abre um POPUP —
 * decisao do dono: compor campanha e' acao pontual, nao merece aba permanente.
 * A antiga aba Carteira saiu (sera refeita de outra forma); o backend da
 * carteira continua intacto e alimenta o card de saldo.
 */
export default function PainelSMS({ canal = "sms" }) {
  const [novaAberta, setNovaAberta] = useState(false);
  const [campanhaAberta, setCampanhaAberta] = useState(null);
  // Termos de uso: null = carregando; {aceito,...} depois. Enquanto nao ha
  // aceite, "Nova campanha" fica travado (e o backend recusa criar tambem).
  const [termos, setTermos] = useState(null);
  const [termosAbertos, setTermosAbertos] = useState(false);

  useEffect(() => {
    setTermos(null); // troca de pilula: recarrega o aceite DAQUELE canal
    smsApi.termos(canal).then(setTermos).catch(() => setTermos({ aceito: false }));
  }, [canal]);
  // Muda a cada disparo concluido pra forcar o dashboard a recarregar.
  const [versao, setVersao] = useState(0);

  // Pagina atras do popup nao rola: sem isso a roda do mouse "vaza" e o
  // usuario perde o popup de vista.
  useEffect(() => {
    document.body.style.overflow = novaAberta ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [novaAberta]);

  // Clique fora (ou no X) pergunta antes de descartar: campanha meio montada
  // sumindo por um clique perdido e' o tipo de raiva que nao volta. Popup do
  // SISTEMA, nao o confirm() do navegador (pedido do dono).
  const [confirmSaida, setConfirmSaida] = useState(false);
  const fecharNova = () => setConfirmSaida(true);

  // Toast de sucesso: aparece, informa e SOME sozinho em 5s (pedido do dono).
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const mostrarToast = (msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const toastEl = toast && (
    <div className="fixed bottom-4 right-4 z-[90] flex items-start gap-3 rounded-xl border bg-background shadow-2xl px-4 py-3 max-w-sm">
      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
      <p className="text-sm">{toast}</p>
    </div>
  );

  if (campanhaAberta) {
    return (
      <>
        <RelatorioCampanhaSMS
          campanhaId={campanhaAberta}
          onVoltar={() => setCampanhaAberta(null)}
          onCancelada={(creditos) => {
            setCampanhaAberta(null);
            setVersao((v) => v + 1);
            mostrarToast(`Agendamento cancelado — ${creditos.toLocaleString("pt-BR")} créditos devolvidos à carteira.`);
          }}
        />
        {toastEl}
      </>
    );
  }

  return (
    <div className="space-y-5">
      {/* Sem titulo "Visao geral": a pagina ja se chama Disparos em massa e
          repetir cabecalho so' empurrava o conteudo pra baixo. */}
      {termos && !termos.aceito && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 flex flex-wrap items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-amber-500 shrink-0" />
          <p className="text-sm flex-1 min-w-[220px]">
            <strong>Antes da primeira campanha {canal === "voz" ? "de Ligação" : "de SMS"}</strong>,
            leia e aceite os Termos de Uso {canal === "voz" ? "das Ligações" : "do SMS"} —
            regras de envio, estatísticas e limites de responsabilidade.
          </p>
          <button onClick={() => setTermosAbertos(true)}
            className="rounded-lg bg-amber-500 text-black px-4 py-2 text-sm font-semibold hover:bg-amber-400 transition-colors">
            Ler e aceitar os termos
          </button>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <button onClick={() => termos?.aceito && setNovaAberta(true)}
          disabled={!termos?.aceito}
          title={termos?.aceito ? undefined
            : `Aceite os Termos de Uso ${canal === "voz" ? "das Ligações" : "do SMS"} para liberar`}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
          {termos?.aceito ? <PlusCircle className="w-4 h-4" /> : <Lock className="w-4 h-4" />} Nova campanha
        </button>
      </div>

      <DashboardSMS key={canal} canal={canal} versao={versao} onAbrirCampanha={setCampanhaAberta} />

      {/* --------------------------------------------------------- popup */}
      {novaAberta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) fecharNova(); }}>
          {/* Altura travada em 92vh: cabecalho e rodape ficam fixos e SO' o
              miolo rola — antes o overlay inteiro rolava e parecia scroll da
              pagina. */}
          <div className="relative flex flex-col w-full max-w-5xl max-h-[92vh] rounded-2xl border bg-background shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b px-5 py-3.5 bg-background shrink-0">
              <h3 className="font-semibold">
                {canal === "voz" ? "Nova campanha de Ligação" : "Nova campanha de SMS"}
              </h3>
              <button onClick={fecharNova} aria-label="Fechar"
                className="rounded-lg p-1.5 hover:bg-muted transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <NovaCampanhaSMS canal={canal}
              onConcluida={(id, msg) => {
                setNovaAberta(false);
                setVersao((v) => v + 1);
                setCampanhaAberta(id);
                if (msg) mostrarToast(msg);
              }}
            />
          </div>

          {confirmSaida && (
            <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
              onClick={(e) => { if (e.target === e.currentTarget) setConfirmSaida(false); }}>
              <div className="w-full max-w-sm rounded-2xl border bg-background shadow-2xl p-6 text-center space-y-3">
                <span className="mx-auto grid place-items-center w-12 h-12 rounded-full bg-amber-500/10">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </span>
                <h4 className="font-semibold text-lg">Tem certeza que deseja sair?</h4>
                <p className="text-sm text-muted-foreground">
                  A campanha que você está montando será perdida.
                </p>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setConfirmSaida(false)}
                    className="flex-1 rounded-lg bg-primary text-primary-foreground py-2.5 text-sm font-semibold">
                    Continuar editando
                  </button>
                  <button onClick={() => { setConfirmSaida(false); setNovaAberta(false); }}
                    className="flex-1 rounded-lg border py-2.5 text-sm font-medium hover:bg-muted transition-colors">
                    Sair e descartar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {termos?.aceito && (
        <p className="text-center text-xs text-muted-foreground pt-1 pb-2">
          Termos de Uso {canal === "voz" ? "das Ligações" : "do SMS"} aceitos
          {termos.aceito_por ? ` por ${termos.aceito_por}` : ""}
          {termos.aceito_em ? ` em ${dataBr(termos.aceito_em)}` : ""} ·{" "}
          <button onClick={() => setTermosAbertos(true)} className="underline hover:text-foreground">
            ler novamente
          </button>
        </p>
      )}

      {termosAbertos && (
        <TermosDisparos
          canal={canal}
          somenteLeitura={!!termos?.aceito}
          onFechar={() => setTermosAbertos(false)}
          onAceito={(r) => {
            setTermos(r);
            setTermosAbertos(false);
            mostrarToast(canal === "voz" ? "Termos aceitos — as ligações estão liberadas." : "Termos aceitos — o SMS está liberado.");
          }}
        />
      )}
      {toastEl}
    </div>
  );
}
